// PropertyFilterPanel — canonical .search-filter offcanvas + .sf-form.
//
// CRITICAL (per R1 audit + R2 STEP R2.6):
//   * modernize.css owns subtype-aware fieldset visibility via the
//     :has() selector at modernize.css:6937-6948. That selector keys
//     on `input[name=property_type][value=…]:checked` and applies
//     display rules to `.sf-group[data-visible-for=…]`.
//   * Therefore: the `data-visible-for` attributes MUST appear
//     exactly as in design/search.html, and the property_type
//     checkboxes MUST keep `name=property_type` and the canonical
//     `value` strings. React only re-renders the DOM — it does NOT
//     orchestrate visibility.
//   * The form root MUST be `.sf-form`. The :has() selector is
//     scoped to that class.
//
// Backend transport reconciliation (per filters.py PropertyFilter):
//   property_type (multi), deal_type, currency, region_code /
//   settlement_code / address_object / local_area,
//   min_price/max_price, rooms_min/rooms_max,
//   min_total_area/max_total_area, min_land_area/max_land_area,
//   floor_min/floor_max (R7.9b), land_purpose (R7.9b),
//   suitable_for (R7.9b), has_recovery_program, has_housing_program,
//   feature (single id), search (DRF SearchFilter — title +
//   description_public).
//
// FEATURE-FILTER NOTE (R7.9b — classification D, product-unresolved):
//   * The frontend FEATURES constant hardcodes 12 English slugs
//     (elevator, ac, …) with no canonical mapping to backend
//     Feature.id values. Backend has no slug column, no seed
//     fixture, no admin-driven taxonomy.
//   * Per Backend-Derivable UI Rule classification D: frontend MUST
//     NOT invent a slug↔id mapping. Until product resolves the
//     taxonomy decision, the .sf-feat-grid inputs are rendered
//     `disabled` and nothing is transported.
//   * The canonical topology (.sf-feat-grid, .sf-feat, icons, labels,
//     order) is preserved verbatim — modernize.css owns layout and
//     the design region must remain reachable when wiring lands.
//
// Ownership:
//   * All filter state is owned by the parent (the consuming
//     ScreenContent widget) and passed in as a controlled-form bag.
//   * This widget is presentation-only and dispatch-free — per
//     "Presentation Primitive Rules" in CLAUDE.md.

import { useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";

import CanonicalRegionFilterSelect from "./CanonicalRegionFilterSelect";
import CanonicalSettlementFilterSelect from "./CanonicalSettlementFilterSelect";
import GeoAddressObjectAutocomplete from "../../geo/widgets/GeoAddressObjectAutocomplete";
import { getGeoAddressObject } from "../../geo/api/geo";
import {
  PROPERTY_COMPENSATION_CURRENCY_FILTERS,
  PROPERTY_COMPENSATION_FILTERS,
  validatePropertyCompensationFilters,
} from "../lib/searchFilterContract";

/**
 * Dispose a Bootstrap Offcanvas instance on the portaled panel without
 * creating a new instance. Clears orphan backdrop + body scroll lock
 * artefacts left after unmount / route change.
 *
 * Body/backdrop cleanup always runs (even when panelEl is missing) so
 * route transitions cannot leave scroll-lock artefacts behind.
 */
export function disposePortaledOffcanvas(panelEl) {
  if (typeof document === "undefined") return;

  if (panelEl) {
    const OffcanvasApi = typeof window !== "undefined"
      ? window.bootstrap?.Offcanvas
      : null;

    if (OffcanvasApi?.getInstance) {
      const instance = OffcanvasApi.getInstance(panelEl);
      if (instance) {
        try {
          instance.hide();
        } catch (_err) {
          /* already hidden / detaching */
        }
        try {
          instance.dispose();
        } catch (_err) {
          /* already disposed */
        }
      }
    }
  }

  document.querySelectorAll(".offcanvas-backdrop").forEach((node) => {
    node.parentNode?.removeChild(node);
  });

  document.body.classList.remove("offcanvas-open");
  document.body.style.removeProperty("overflow");
  document.body.style.removeProperty("padding-right");
}

const PROPERTY_TYPES = [
  { value: "apartment", label: "Квартира", icon: "fa-solid fa-building" },
  { value: "house", label: "Будинок", icon: "fa-solid fa-house-chimney" },
  { value: "land", label: "Земля", icon: "fa-solid fa-mountain-sun" },
  { value: "commercial", label: "Комерційна", icon: "fa-solid fa-store" },
  { value: "industrial", label: "Промислова", icon: "fa-solid fa-industry" },
];

const LAND_PURPOSES = [
  { value: "", label: "Усі" },
  { value: "residential", label: "Під житлову забудову" },
  { value: "agricultural", label: "Сільськогосподарського призначення" },
  { value: "commercial", label: "Комерційного використання" },
  { value: "industrial", label: "Промислового використання" },
  { value: "recreational", label: "Рекреаційне" },
  { value: "garden", label: "Садова / дачна" },
];

const COMMERCIAL_SUITABILITIES = [
  { value: "", label: "Усі" },
  { value: "office", label: "Офіс" },
  { value: "retail", label: "Торгівля / магазин" },
  { value: "warehouse", label: "Склад" },
  { value: "restaurant", label: "Ресторан / кафе" },
  { value: "hotel", label: "Готель" },
  { value: "production", label: "Виробництво" },
  { value: "mixed", label: "Змішане використання" },
];

// RIC-4.3 — building-characteristic enums. Values mirror the backend
// TextChoices verbatim (WallType / HeatingType / ConditionType /
// FurnitureType in models.py) — NO new values invented; only the display
// labels are localized, exactly as LAND_PURPOSES / COMMERCIAL_SUITABILITIES
// do. Leading {value:""} = "Усі" (no filter).
const WALL_TYPES = [
  { value: "", label: "Усі" },
  { value: "brick", label: "Цегла" },
  { value: "blocks", label: "Блоки" },
  { value: "monolith", label: "Моноліт" },
  { value: "panel", label: "Панель" },
  { value: "wood", label: "Дерево" },
  { value: "other", label: "Інше" },
];

const HEATING_TYPES = [
  { value: "", label: "Усі" },
  { value: "central", label: "Центральне" },
  { value: "gas", label: "Індивідуальне газове" },
  { value: "electric", label: "Індивідуальне електричне" },
  { value: "stove", label: "Пічне" },
  { value: "solar", label: "Сонячне" },
  { value: "solid", label: "Твердопаливне" },
  { value: "roof_boiler", label: "Дахова котельня" },
  { value: "other", label: "Інше" },
];

const CONDITION_TYPES = [
  { value: "", label: "Усі" },
  { value: "none", label: "Без ремонту" },
  { value: "raw", label: "Чорнова / коробка" },
  { value: "needs_repair", label: "Потребує ремонту" },
  { value: "cosmetic", label: "Косметичний ремонт" },
  { value: "euro", label: "Євроремонт" },
  { value: "designer", label: "Дизайнерський" },
];

const FURNITURE_TYPES = [
  { value: "", label: "Усі" },
  { value: "full", label: "Повністю мебльована" },
  { value: "partial", label: "Частково мебльована" },
  { value: "none", label: "Без меблів" },
];

const FEATURES = [
  { value: "elevator", label: "Ліфт", icon: "fa-solid fa-elevator" },
  { value: "ac", label: "Кондиціонер", icon: "fa-solid fa-snowflake" },
  { value: "fireplace", label: "Камін", icon: "fa-solid fa-fire" },
  { value: "security", label: "Охорона", icon: "fa-solid fa-shield-halved" },
  { value: "playground", label: "Дит. майданчик", icon: "fa-solid fa-children" },
  { value: "parking", label: "Парковка", icon: "fa-solid fa-square-parking" },
  { value: "garage", label: "Гараж", icon: "fa-solid fa-warehouse" },
  { value: "balcony", label: "Балкон", icon: "fa-solid fa-mountain-sun" },
  { value: "furniture", label: "Меблі", icon: "fa-solid fa-couch" },
  { value: "generator", label: "Генератор", icon: "fa-solid fa-bolt" },
  { value: "internet", label: "Інтернет", icon: "fa-solid fa-wifi" },
  { value: "cctv", label: "Відеонагляд", icon: "fa-solid fa-video" },
];

function FilterSection({ title, titleId, children }) {
  return (
    <section className="sf-section" aria-labelledby={titleId}>
      <h3 className="sf-section__title" id={titleId}>
        {title}
      </h3>
      <div className="sf-section__body">{children}</div>
    </section>
  );
}

function PropertyFilterPanel({
  value,
  onChange,
  onSubmit,
  onReset,
  offcanvasId = "offcanvasFilter",
}) {
  const panelRef = useRef(null);

  // Capture the panel node at commit time. Portal teardown can clear
  // refs / remove #offcanvasId before a passive effect cleanup runs;
  // useLayoutEffect + captured el keep dispose/hide reachable.
  useLayoutEffect(() => {
    const id = offcanvasId;
    const capturedEl = panelRef.current || document.getElementById(id);
    return () => {
      disposePortaledOffcanvas(document.getElementById(id) || capturedEl);
    };
  }, [offcanvasId]);

  // The controlled-form bag — parent owns the values.
  const v = value || {};
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const addressHydrateAttemptedRef = useRef(new Set());

  const setField = (key, next) => {
    onChange((current) => ({ ...(current || {}), [key]: next }));
  };

  useEffect(() => {
    const settlement = (v.settlement_code || "").trim();
    const objectId = String(v.address_object || "").trim();
    const verified = Boolean(v.address_object_name_verified);
    if (!settlement || !objectId || verified) {
      return undefined;
    }
    const key = `${settlement}:${objectId}`;
    if (addressHydrateAttemptedRef.current.has(key)) {
      return undefined;
    }
    addressHydrateAttemptedRef.current.add(key);
    let cancelled = false;
    const controller = new AbortController();
    getGeoAddressObject({
      settlementCode: settlement,
      id: objectId,
      signal: controller.signal,
    })
      .then((row) => {
        if (cancelled) return;
        onChangeRef.current((current) => {
          if (String(current?.address_object || "") !== objectId) return current;
          if ((current?.settlement_code || "").trim() !== settlement) {
            return current;
          }
          const rowId = row?.id != null ? String(row.id) : "";
          const canonicalName = rowId === objectId ? (row.name || "").trim() : "";
          return {
            ...current,
            address_object_name: canonicalName,
            address_object_name_verified: Boolean(canonicalName),
          };
        });
      })
      .catch(() => {
        if (cancelled) return;
        onChangeRef.current((current) => {
          if (String(current?.address_object || "") !== objectId) return current;
          if ((current?.settlement_code || "").trim() !== settlement) {
            return current;
          }
          return {
            ...current,
            address_object_name: "",
            address_object_name_verified: false,
          };
        });
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [v.settlement_code, v.address_object, v.address_object_name_verified]);

  // RIC-4.1A.3 — frontend-only rooms range guard. Blocks apply when a
  // numeric min exceeds a numeric max (both fields are optional).
  const roomsMinNum =
    v.rooms_min !== "" && v.rooms_min !== null && v.rooms_min !== undefined
      ? Number(v.rooms_min)
      : null;
  const roomsMaxNum =
    v.rooms_max !== "" && v.rooms_max !== null && v.rooms_max !== undefined
      ? Number(v.rooms_max)
      : null;
  const roomsInvalid =
    roomsMinNum !== null &&
    roomsMaxNum !== null &&
    Number.isFinite(roomsMinNum) &&
    Number.isFinite(roomsMaxNum) &&
    roomsMinNum > roomsMaxNum;

  const compensationValidation = validatePropertyCompensationFilters(v);
  const compensationInvalid = Boolean(compensationValidation.errors.compensation);
  const currencyInvalid = Boolean(compensationValidation.errors.compensation_currency);
  // Currency disabled only for the valid "none + no currency" state.
  // When none+currency is present (invalid URL), show currency as invalid instead.
  const compensationCurrencyDisabled = (v.compensation === "none") && !currencyInvalid;

  const toggleFeature = (feature) => {
    const current = Array.isArray(v.features) ? v.features : [];
    const next = current.includes(feature)
      ? current.filter((x) => x !== feature)
      : [...current, feature];
    setField("features", next);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (roomsInvalid) return; // RIC-4.1A.3 — block invalid rooms range
    if (onSubmit) onSubmit();
  };

  const handleReset = (event) => {
    event.preventDefault();
    if (onReset) onReset();
  };

  // Issue #181 — structural stacking repair: keep the trigger in
  // `.search-filter` (map overlay sibling) but portal the Bootstrap
  // offcanvas panel to document.body so it is not trapped inside the
  // local `.search-filter` stacking context created with
  // `.search-toolbar`. Bootstrap backdrop/panel layers stay canonical.
  const panel = (
      <div
        ref={panelRef}
        className="offcanvas offcanvas-start search-filter__panel"
        tabIndex="-1"
        id={offcanvasId}
        aria-labelledby={`${offcanvasId}-label`}
        data-search-filter-panel="ported"
      >
        <header className="offcanvas-header search-filter__header">
          <h2
            className="search-filter__title"
            id={`${offcanvasId}-label`}
          >
            Фільтр
          </h2>
          <button
            type="button"
            className="search-filter__close"
            data-bs-dismiss="offcanvas"
            data-bs-target={`#${offcanvasId}`}
            aria-label="Закрити"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>

        <div className="offcanvas-body search-filter__body">
          <form
            className="sf-form"
            onSubmit={handleSubmit}
            onReset={handleReset}
          >
            {/* Free-text search — wired to DRF SearchFilter `?search=` */}
            <label className="sf-search">
              <i
                className="fa-solid fa-magnifying-glass"
                aria-hidden="true"
              />
              <input
                type="search"
                name="q"
                value={v.q || ""}
                onChange={(event) => setField("q", event.target.value)}
                placeholder="Заголовок, адреса, ЖК…"
                aria-label="Пошук"
              />
            </label>

            <FilterSection title="Основне" titleId={`${offcanvasId}-section-basics`}>
            <fieldset className="sf-group">
              <legend>Тип угоди</legend>
              <div className="sf-segmented" role="radiogroup">
                <label>
                  <input
                    type="radio"
                    name="deal_type"
                    value=""
                    checked={!v.deal_type}
                    onChange={() => setField("deal_type", "")}
                  />
                  <span>Усі</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="deal_type"
                    value="sale"
                    checked={v.deal_type === "sale"}
                    onChange={() => setField("deal_type", "sale")}
                  />
                  <span>Продаж</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="deal_type"
                    value="rent"
                    checked={v.deal_type === "rent"}
                    onChange={() => setField("deal_type", "rent")}
                  />
                  <span>Оренда</span>
                </label>
              </div>
            </fieldset>

            {/* Property type (single-select) — drives subtype visibility
                via modernize.css :has(.sf-chips input[value=…]:checked).
                Radios MUST keep name=property_type, the canonical `value`
                strings, and the `.sf-chips` wrapper so the frozen CSS
                selector keeps matching. The "Усі" radio (value="")
                restores the no-type state (radios can't be un-clicked). */}
            <fieldset className="sf-group">
              <legend>Тип нерухомості</legend>
              <div className="sf-chips">
                <label className="sf-chip">
                  <input
                    type="radio"
                    name="property_type"
                    value=""
                    checked={!v.property_type}
                    onChange={() => setField("property_type", "")}
                  />
                  <i className="fa-solid fa-border-all" aria-hidden="true" />
                  <span>Усі</span>
                </label>
                {PROPERTY_TYPES.map((option) => (
                  <label key={option.value} className="sf-chip">
                    <input
                      type="radio"
                      name="property_type"
                      value={option.value}
                      checked={v.property_type === option.value}
                      onChange={() => setField("property_type", option.value)}
                    />
                    <i className={option.icon} aria-hidden="true" />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            </FilterSection>

            <FilterSection title="Локація" titleId={`${offcanvasId}-section-location`}>
            {/* Location — canonical Region + settlement + structured street/local_area */}
            <fieldset className="sf-group sf-group--location">
              <legend className="visually-hidden">Локація</legend>
              <div className="sf-row">
                <div className="sf-field">
                  <label htmlFor="sf-region-code">Область / регіон</label>
                  <CanonicalRegionFilterSelect
                    id="sf-region-code"
                    filters={v}
                    onChange={onChange}
                  />
                </div>
              </div>
              <div className="sf-row">
                <div className="sf-field">
                  <CanonicalSettlementFilterSelect
                    id="sf-settlement-code"
                    filters={v}
                    onChange={onChange}
                  />
                </div>
              </div>
              <div className="sf-row">
                <div className="sf-field">
                  <GeoAddressObjectAutocomplete
                    id="sf-address-object"
                    settlementCode={(v.settlement_code || "").trim()}
                    enabled={Boolean((v.settlement_code || "").trim())}
                    selectedAddressObject={
                      v.address_object
                      && v.address_object_name_verified
                      && (v.address_object_name || "").trim()
                        ? {
                          id: Number(v.address_object),
                          name: (v.address_object_name || "").trim(),
                        }
                        : null
                    }
                    onSelect={(obj) => {
                      onChange((current) => ({
                        ...(current || {}),
                        address_object: obj?.id != null ? String(obj.id) : "",
                        address_object_name: obj?.name || "",
                        address_object_name_verified: Boolean(obj?.id != null && obj?.name),
                      }));
                    }}
                    disabled={!((v.settlement_code || "").trim())}
                  />
                </div>
              </div>
              <div className="sf-row">
                <div className="sf-field">
                  <label htmlFor="sf-local-area">Місцевість / район</label>
                  <input
                    id="sf-local-area"
                    name="local_area"
                    type="text"
                    className="form-control"
                    placeholder="Печерський"
                    value={v.local_area || ""}
                    onChange={(event) =>
                      setField("local_area", event.target.value)
                    }
                  />
                </div>
              </div>
            </fieldset>
            </FilterSection>

            <FilterSection title="Ціна та партнерство" titleId={`${offcanvasId}-section-price`}>
            {/* Price range + currency */}
            <fieldset className="sf-group">
              <legend>
                Ціна
              </legend>
              <div className="sf-range">
                <input
                  type="number"
                  name="price_min"
                  placeholder="Від"
                  min="0"
                  className="form-control"
                  aria-label="Ціна від"
                  value={v.price_min || ""}
                  onChange={(event) =>
                    setField("price_min", event.target.value)
                  }
                />
                <span className="sf-range__sep" aria-hidden="true">—</span>
                <input
                  type="number"
                  name="price_max"
                  placeholder="До"
                  min="0"
                  className="form-control"
                  aria-label="Ціна до"
                  value={v.price_max || ""}
                  onChange={(event) =>
                    setField("price_max", event.target.value)
                  }
                />
                <select
                  name="currency"
                  className="form-select sf-currency"
                  aria-label="Валюта"
                  value={v.currency || "USD"}
                  onChange={(event) =>
                    setField("currency", event.target.value)
                  }
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="UAH">UAH</option>
                </select>
              </div>
            </fieldset>

            {/* Partner compensation — always visible; URL via searchFilterContract. */}
            <fieldset className="sf-group">
              <legend>Винагорода партнеру</legend>
              <select
                name="compensation"
                className="form-select"
                value={compensationInvalid ? "__invalid__" : (v.compensation || "")}
                aria-invalid={compensationInvalid ? "true" : undefined}
                data-testid="property-filter-compensation"
                onChange={(event) => {
                  const nextCompensation = event.target.value;
                  onChange((current) => ({
                    ...(current || {}),
                    compensation: nextCompensation,
                    compensation_currency:
                      nextCompensation === "none"
                        ? ""
                        : (current?.compensation_currency || ""),
                  }));
                }}
              >
                {compensationInvalid ? (
                  <option value="__invalid__" disabled>
                    Некоректне значення: {v.compensation}
                  </option>
                ) : null}
                {PROPERTY_COMPENSATION_FILTERS.map((opt) => (
                  <option key={opt.value || "all-compensation"} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {compensationInvalid ? (
                <p
                  className="sf-field-error"
                  role="alert"
                  data-testid="property-filter-compensation-error"
                >
                  {compensationValidation.errors.compensation}
                </p>
              ) : null}
            </fieldset>

            <fieldset
              className="sf-group"
              aria-disabled={compensationCurrencyDisabled ? "true" : undefined}
            >
              <legend>Валюта винагороди</legend>
              <select
                name="compensation_currency"
                className="form-select"
                value={currencyInvalid ? "__invalid_currency__" : (v.compensation_currency || "")}
                disabled={compensationCurrencyDisabled}
                aria-invalid={currencyInvalid ? "true" : undefined}
                data-testid="property-filter-compensation-currency"
                onChange={(event) =>
                  setField("compensation_currency", event.target.value)
                }
              >
                {currencyInvalid ? (
                  <option value="__invalid_currency__" disabled>
                    Некоректне значення: {v.compensation_currency}
                  </option>
                ) : null}
                {PROPERTY_COMPENSATION_CURRENCY_FILTERS.map((opt) => (
                  <option
                    key={opt.value || "all-compensation-currency"}
                    value={opt.value}
                  >
                    {opt.label}
                  </option>
                ))}
              </select>
              {currencyInvalid ? (
                <p
                  className="sf-field-error"
                  role="alert"
                  data-testid="property-filter-compensation-currency-error"
                >
                  {compensationValidation.errors.compensation_currency}
                </p>
              ) : null}
            </fieldset>
            </FilterSection>

            <FilterSection title="Характеристики об'єкта" titleId={`${offcanvasId}-section-specs`}>
            {/* Area range — single area control whose backend dimension is
                routed by property_type in buildBackendParams (RIC-4.1A):
                land → min/max_land_area (plot area), otherwise
                min/max_total_area. RIC-4.2: the legend adapts so the Land
                case reads as plot area; the control itself is unchanged. */}
            <fieldset className="sf-group">
              <legend>
                {v.property_type === "land" ? (
                  <>Площа ділянки, м<sup>2</sup></>
                ) : (
                  <>Площа, м<sup>2</sup></>
                )}
              </legend>
              <div className="sf-range">
                <input
                  type="number"
                  name="area_min"
                  placeholder="Від"
                  min="0"
                  className="form-control"
                  aria-label="Площа від"
                  value={v.area_min || ""}
                  onChange={(event) =>
                    setField("area_min", event.target.value)
                  }
                />
                <span className="sf-range__sep" aria-hidden="true">—</span>
                <input
                  type="number"
                  name="area_max"
                  placeholder="До"
                  min="0"
                  className="form-control"
                  aria-label="Площа до"
                  value={v.area_max || ""}
                  onChange={(event) =>
                    setField("area_max", event.target.value)
                  }
                />
              </div>
            </fieldset>

            {/* Rooms — residential only (data-visible-for is CSS-driven).
                Direct rooms_min / rooms_max range inputs (RIC-4.1A.3) —
                no chip selector, no band/synthetic interpretation. */}
            <fieldset className="sf-group" data-visible-for="residential">
              <legend>Кімнат</legend>
              <div className="sf-range">
                <input
                  type="number"
                  name="rooms_min"
                  placeholder="Від"
                  min="0"
                  className="form-control"
                  aria-label="Кімнат від"
                  value={v.rooms_min || ""}
                  onChange={(event) =>
                    setField("rooms_min", event.target.value)
                  }
                />
                <span className="sf-range__sep" aria-hidden="true">—</span>
                <input
                  type="number"
                  name="rooms_max"
                  placeholder="До"
                  min="0"
                  className="form-control"
                  aria-label="Кімнат до"
                  value={v.rooms_max || ""}
                  onChange={(event) =>
                    setField("rooms_max", event.target.value)
                  }
                />
              </div>
              {roomsInvalid ? (
                <p className="sf-field-error" role="alert">
                  «Від» не може бути більшим за «До».
                </p>
              ) : null}
            </fieldset>

            {/* Floor range — residential only. Wired to backend
                floor_min / floor_max (R7.9b) — custom Q across
                residential ∪ commercial in filters.py. */}
            <fieldset className="sf-group" data-visible-for="residential">
              <legend>Поверх</legend>
              <div className="sf-range">
                <input
                  type="number"
                  name="floor_min"
                  placeholder="Від"
                  className="form-control"
                  aria-label="Поверх від"
                  value={v.floor_min || ""}
                  onChange={(event) =>
                    setField("floor_min", event.target.value)
                  }
                />
                <span className="sf-range__sep" aria-hidden="true">—</span>
                <input
                  type="number"
                  name="floor_max"
                  placeholder="До"
                  className="form-control"
                  aria-label="Поверх до"
                  value={v.floor_max || ""}
                  onChange={(event) =>
                    setField("floor_max", event.target.value)
                  }
                />
              </div>
            </fieldset>

            {/* Residential complex (ЖК) — RIC-4.3. icontains on
                residential__residential_complex. data-visible-for shows it
                for residential types (apartment / house). */}
            <fieldset className="sf-group" data-visible-for="residential">
              <legend>Житловий комплекс</legend>
              <input
                name="residential_complex"
                type="text"
                className="form-control"
                placeholder="Манхеттен, Липки, Паркова Алея…"
                aria-label="Житловий комплекс"
                value={v.residential_complex || ""}
                onChange={(event) =>
                  setField("residential_complex", event.target.value)
                }
              />
            </fieldset>

            {/* Land purpose — land only. Wired to backend land_purpose
                (R7.9b) — ChoiceFilter on land__purpose. */}
            <fieldset className="sf-group" data-visible-for="land">
              <legend>Цільове призначення</legend>
              <select
                name="land_purpose"
                className="form-select"
                value={v.land_purpose || ""}
                onChange={(event) =>
                  setField("land_purpose", event.target.value)
                }
              >
                {LAND_PURPOSES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </fieldset>

            {/* Cadastral number — land only. RIC-4.3. icontains on
                land_parcels__cadastral_number (free-text, non-unique). */}
            <fieldset className="sf-group" data-visible-for="land">
              <legend>Кадастровий номер</legend>
              <input
                name="cadastral_number"
                type="text"
                className="form-control"
                placeholder="1234567890:12:345:0678"
                aria-label="Кадастровий номер"
                value={v.cadastral_number || ""}
                onChange={(event) =>
                  setField("cadastral_number", event.target.value)
                }
              />
            </fieldset>

            {/* Commercial suitability — commercial only. Wired to
                backend suitable_for (R7.9b) — ChoiceFilter on
                commercial__suitable_for. */}
            <fieldset className="sf-group" data-visible-for="commercial">
              <legend>Призначення</legend>
              <select
                name="suitable_for"
                className="form-select"
                value={v.suitable_for || ""}
                onChange={(event) =>
                  setField("suitable_for", event.target.value)
                }
              >
                {COMMERCIAL_SUITABILITIES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </fieldset>

            {/* Building characteristics — walls / heating / condition /
                furniture exist on residential AND commercial rows, so they
                apply to every non-land type. RIC-4.3A added the `building`
                visibility bucket (apartment/house/commercial/industrial) to
                the modernize.css :has() block; data-visible-for="building"
                hides these when property_type=land (LandProperty has no such
                columns). Enum filters, single-select. */}
            <fieldset className="sf-group" data-visible-for="building">
              <legend>Стіни</legend>
              <select
                name="walls"
                className="form-select"
                value={v.walls || ""}
                onChange={(event) => setField("walls", event.target.value)}
              >
                {WALL_TYPES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </fieldset>

            <fieldset className="sf-group" data-visible-for="building">
              <legend>Опалення</legend>
              <select
                name="heating"
                className="form-select"
                value={v.heating || ""}
                onChange={(event) => setField("heating", event.target.value)}
              >
                {HEATING_TYPES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </fieldset>

            <fieldset className="sf-group" data-visible-for="building">
              <legend>Стан</legend>
              <select
                name="condition"
                className="form-select"
                value={v.condition || ""}
                onChange={(event) => setField("condition", event.target.value)}
              >
                {CONDITION_TYPES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </fieldset>

            <fieldset className="sf-group" data-visible-for="building">
              <legend>Меблі</legend>
              <select
                name="furniture"
                className="form-select"
                value={v.furniture || ""}
                onChange={(event) => setField("furniture", event.target.value)}
              >
                {FURNITURE_TYPES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </fieldset>
            </FilterSection>

            <FilterSection title="Додатково" titleId={`${offcanvasId}-section-extra`}>
            {/* State programs — wired to has_housing_program /
                has_recovery_program. */}
            <fieldset className="sf-group">
              <legend>Державні програми</legend>
              <div className="sf-checks">
                <label className="sf-check">
                  <input
                    type="checkbox"
                    name="has_housing_program"
                    checked={Boolean(v.has_housing_program)}
                    onChange={(event) =>
                      setField("has_housing_program", event.target.checked)
                    }
                  />
                  <i className="fa-solid fa-house-flag" aria-hidden="true" />
                  <span>єОселя</span>
                </label>
                <label className="sf-check">
                  <input
                    type="checkbox"
                    name="has_recovery_program"
                    checked={Boolean(v.has_recovery_program)}
                    onChange={(event) =>
                      setField("has_recovery_program", event.target.checked)
                    }
                  />
                  <i className="fa-solid fa-rotate" aria-hidden="true" />
                  <span>єВідновлення</span>
                </label>
              </div>
            </fieldset>

            {/* Features grid — classification D (product-unresolved).
                See file header note. Inputs are rendered `disabled`
                because the frontend slug taxonomy
                (elevator/ac/fireplace/…) has no canonical mapping to
                backend Feature.id. Canonical .sf-feat-grid topology
                is preserved verbatim — when product resolves
                slug↔name semantics, the only change needed here is
                removing the `disabled` attribute. */}
            <fieldset className="sf-group" disabled aria-disabled="true">
              <legend>Особливості</legend>
              <div className="sf-feat-grid">
                {FEATURES.map((feature) => {
                  const checked =
                    Array.isArray(v.features) &&
                    v.features.includes(feature.value);
                  return (
                    <label key={feature.value} className="sf-feat">
                      <input
                        type="checkbox"
                        name="features"
                        value={feature.value}
                        checked={checked}
                        onChange={() => toggleFeature(feature.value)}
                      />
                      <i className={feature.icon} aria-hidden="true" />
                      {feature.label}
                    </label>
                  );
                })}
              </div>
            </fieldset>
            </FilterSection>

            <div className="sf-actions">
              <button type="reset" className="action-btn">
                <i
                  className="fa-solid fa-arrow-rotate-left"
                  aria-hidden="true"
                />
                Скинути
              </button>
              <button
                type="submit"
                className="btn-more"
                disabled={roomsInvalid}
              >
                Застосувати
                <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
              </button>
            </div>
          </form>
        </div>
      </div>
  );

  return (
    <>
      <aside className="search-filter">
        <button
          type="button"
          className="search-filter__open"
          data-bs-toggle="offcanvas"
          data-bs-target={`#${offcanvasId}`}
          aria-controls={offcanvasId}
        >
          <i className="fa-solid fa-sliders" aria-hidden="true" />
          Фільтр
        </button>
      </aside>
      {typeof document !== "undefined"
        ? createPortal(panel, document.body)
        : panel}
    </>
  );
}

export default PropertyFilterPanel;
